import { useComputed, useModal } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import React, { createContext, useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import {
  DesignPackageLocation,
  SaveDesignPackageInput,
  SaveDesignPackageOptionInput,
  SaveDesignPackageOptionPrereqInput,
} from "src/generated/graphqlTypes";
import { DesignPackageItemSlotEditorDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import {
  EditDesignPackage_DesignPackageFragment,
  NewDesignPackage_GlobalOptionGroupFragment,
  NewDesignPackage_GlobalOptionTypeFragment,
  NewDesignPackage_ReadyPlanOptionFragment,
  useEditDesignPackageQuery,
  useMakeNewDesignPackageMutation,
  useNewDesignPackage_GlobalOptionGroupsQuery,
  usePreselectOptionsFromPlanPackagesQuery,
} from "src/routes/libraries/design-catalog/new-design-catalog-wizard/NewDesignPackage.generated";
import { createDesignPackageUrl } from "src/RouteUrls";
import { fail } from "src/utils";

// Export for testing
export type NewDesignPackageContextValue = {
  formState: ObjectState<NewDesignPackageForm>;
  inFlight: boolean;
  save: () => Promise<void>;
  // All GOGs, both scoped and shared
  globalOptionGroups: NewDesignPackage_GlobalOptionGroupFragment[];
  // Needed when the scoped GOGs don't exist yet (i.e., new design packages)
  globalOptionTypes: NewDesignPackage_GlobalOptionTypeFragment[];
  planPackageOptions: NewDesignPackage_ReadyPlanOptionFragment[];
  /** Denotes Create vs Edit. Expectation: Controls Interior/Exterior on create, which can no longer be updated. */
  isEditMode: boolean;
};

// Export for testing
export const NewDesignPackageContext = createContext<NewDesignPackageContextValue>({
  formState: undefined!, // This should init immediately. Only reason undefined would trigger is if Provider isn't actually wrapping a component when useContext is called, which should never happen
  inFlight: false,
  save: async () => {},
  globalOptionGroups: [],
  globalOptionTypes: [],
  planPackageOptions: [],
  isEditMode: false,
});

export const useNewDesignPackageContext = () => React.useContext(NewDesignPackageContext);

type NewDesignPackageProviderProps = React.PropsWithChildren<{
  maybeDesignPackageId: string | undefined;
  maybeVersionId: string | undefined;
}>;

export function NewDesignPackageProvider(props: NewDesignPackageProviderProps) {
  const { children, maybeDesignPackageId, maybeVersionId } = props;
  const { data: maybeDesignPackage } = useEditDesignPackageQuery({
    skip: !maybeDesignPackageId,
    variables: { id: maybeDesignPackageId!, versionId: maybeVersionId },
  });

  const isEditMode = useMemo(() => !!maybeDesignPackageId, [maybeDesignPackageId]);

  const formState = useFormState({
    config: newDesignPackageFormConfig,
    init: {
      input: maybeDesignPackage?.designPackage,
      // maybeDesignPackage query won't fire during the create-new flow, so map never runs. Use this as a fallback.
      ifUndefined: {
        name: "",
        planPackageIds: [],
        // forces the user to pick
        location: maybeDesignPackage?.designPackage.location.code ?? undefined,
        markets: [],
        options: [],
      },
      map: mapEditDesignPackageToForm,
    },
  });
  const [mutate, { loading: inFlight }] = useMakeNewDesignPackageMutation();
  const { closeModal } = useModal();
  const location =
    useComputed(() => formState.location.value, [formState]) ?? maybeDesignPackage?.designPackage.location.code;
  const { data, loading: optionsLoading } = useNewDesignPackage_GlobalOptionGroupsQuery({
    variables: {
      designPackageId: maybeDesignPackageId ?? "",
      // For new DPs, scoped GOGs won't exist yet (i.e., Interior Scheme and Exterior Scheme), so we need to query
      // the GOTs instead. They will be sent to the BE via the globalOptionTypeId field, and the BE will create the GOGs.
      includeScopedGroups: !!maybeDesignPackageId,
      forInterior: location === DesignPackageLocation.Interior || undefined,
      forExterior: location === DesignPackageLocation.Exterior || undefined,
    },
    skip: !location,
  });
  const history = useHistory();

  /** A DesignPackage should pre-select any options from PlanPackages */
  const planPackageIds = useComputed(() => formState.planPackageIds.value ?? [], [formState]);
  const preselectQuery = usePreselectOptionsFromPlanPackagesQuery({
    variables: { planPackageIds },
    skip: planPackageIds.isEmpty || location !== DesignPackageLocation.Exterior,
  });
  const ppElevationRpos = useMemo(
    () =>
      preselectQuery.data?.planPackages.entities.flatMap((pp) =>
        pp.optionGroups.filter((rpog) => rpog.group.type.isElevation).flatMap((og) => og.options),
      ) ?? [],
    [preselectQuery],
  );
  /** preselect elevation options from plan packages */
  useEffect(() => {
    if (ppElevationRpos.nonEmpty) {
      const existingOptions = maybeDesignPackage?.designPackage.options ?? [];
      // Combine the existing options with the preselected ones
      // They will be grouped by global option id + name, so we can display them together
      // When saving, we'll skip the id since the BE knows how to search for existing options.
      formState.options.set(mapOptionsToForm([...existingOptions, ...ppElevationRpos], location, ppElevationRpos));
    }
  }, [ppElevationRpos, location, formState, maybeDesignPackage?.designPackage.options]);

  const save = useCallback(async () => {
    const { location, options, ...values } = formState.value;
    const result = await mutate({
      variables: {
        input: {
          ...values,
          // Update this design package if we're editing an existing one
          ...(maybeDesignPackage && { id: maybeDesignPackageId }),
          // only touch location if it's a new design package
          ...(!maybeDesignPackage && { location }),
          // filter out options added but not fully filled out
          options: options
            ?.filter((o) => o.id || o.name)
            .map((o) => {
              const { globalOptionId, ...rest } = o;
              return {
                ...rest,
                // Preselected options have been grouped by global option id, and the rpo id has been faked to be unique
                // For those, the GlobalOptionId will be used as the key to findOrCreate on BE
                id: o.id?.startsWith("rpo:") ? o.id : undefined,
                // Also remove globalOptionId from children
                optionPrereqChildren: o.optionPrereqChildren?.map(({ globalOptionId, ...child }) => child),
              };
            }),
        },
      },
      refetchQueries: [DesignPackageItemSlotEditorDocument],
    });
    const newId = result.data?.saveDesignPackage.designPackage.id ?? fail();
    const versionId = result.data?.saveDesignPackage.designPackage.version.id ?? fail();
    history.push(createDesignPackageUrl(newId, versionId));
    closeModal();
  }, [maybeDesignPackage, mutate, formState.value, maybeDesignPackageId, history, closeModal]);

  const globalOptionGroups = useMemo(
    () =>
      [
        ...(data?.scopedGroups ?? []),
        // TODO: Temporarily filter until we scope GOGs that aren't elevation or spec level...
        // But maybe we should filter anyway just in case?
        ...(data?.sharedGroups ?? []).filter((gog) => gog.type.isElevation || gog.type.isSpecLevel),
      ].sortBy((gog) => gog.type.order),
    [data],
  );

  return (
    <NewDesignPackageContext.Provider
      value={{
        formState,
        inFlight: inFlight || optionsLoading,
        save,
        globalOptionGroups,
        globalOptionTypes: data?.types ?? [],
        isEditMode,
        planPackageOptions: ppElevationRpos,
      }}
    >
      {children}
    </NewDesignPackageContext.Provider>
  );
}

/** Internal type for form state that includes globalOptionId for UI logic */
type DesignPackageOptionPrereqFormInput = SaveDesignPackageOptionPrereqInput & { globalOptionId?: string };

export type DesignPackageOptionFormInput = Omit<SaveDesignPackageOptionInput, "optionPrereqChildren"> & {
  globalOptionId?: string;
  optionPrereqChildren?: DesignPackageOptionPrereqFormInput[];
};

/** Form State to span multiple pages */
export type NewDesignPackageForm = Omit<SaveDesignPackageInput, "options"> & {
  options?: DesignPackageOptionFormInput[];
};

const optionPrereqConfig: ObjectConfig<DesignPackageOptionPrereqFormInput> = {
  id: { type: "value" },
  name: { type: "value" },
  // Will only be set if the scoped GOG (i.e., Exterior Scheme) doesn't exist yet
  globalOptionTypeId: { type: "value" },
  globalOptionGroupId: { type: "value" },
  globalOptionId: { type: "value" },
};

const optionConfig: ObjectConfig<DesignPackageOptionFormInput> = {
  id: { type: "value" },
  name: { type: "value" },
  // Will only be set if the scoped GOG (i.e., Interior Scheme) doesn't exist yet
  globalOptionTypeId: { type: "value" },
  globalOptionGroupId: { type: "value" },
  globalOptionId: { type: "value" },
  optionPrereqChildren: { type: "list", config: optionPrereqConfig },
};

export const newDesignPackageFormConfig: ObjectConfig<NewDesignPackageForm> = {
  name: { type: "value", rules: [required] },
  planPackageIds: { type: "value", rules: [required] },
  location: { type: "value", rules: [required] },
  markets: { type: "value" },
  ownerId: { type: "value" },
  options: {
    type: "list",
    config: optionConfig,
  },
};

function isInteriorOption(
  rpo: NewDesignPackage_ReadyPlanOptionFragment,
  location: DesignPackageLocation | undefined | null,
) {
  return location === DesignPackageLocation.Interior && rpo.globalOption.group.forDesignInterior;
}

function isExteriorOption(
  rpo: NewDesignPackage_ReadyPlanOptionFragment,
  location: DesignPackageLocation | undefined | null,
) {
  return location === DesignPackageLocation.Exterior && rpo.globalOption.group.forDesignExterior;
}

// Export for testing
export function mapEditDesignPackageToForm(dp: EditDesignPackage_DesignPackageFragment) {
  return {
    name: dp.name,
    planPackageIds: dp.planPackages.map(({ id }) => id) ?? [],
    markets: dp.markets.map(({ id }) => id) ?? [],
    options: mapOptionsToForm(dp.options, dp.location.code, []),
    ownerId: dp.owner?.id,
  };
}

function mapOptionsToForm(
  rpos: NewDesignPackage_ReadyPlanOptionFragment[],
  location: DesignPackageLocation | undefined | null,
  selectedPlanPackageOptions: NewDesignPackage_ReadyPlanOptionFragment[],
) {
  const childrenOptionsById = rpos
    .filter((rpo) => rpo.active)
    .flatMap((rpo) => rpo.optionPrereqChildren)
    .uniqueByKey("id")
    .keyBy((rpo) => rpo.id);

  const ppRpos = selectedPlanPackageOptions.keyBy((o) => o.id);

  return (
    rpos
      // only display options that are interior or exterior
      .filter((rpo) => isInteriorOption(rpo, location) || isExteriorOption(rpo, location))
      // filter out options that are children of other options
      .filter((rpo) => rpo.active && !childrenOptionsById[rpo.id])
      .sortByKey("createdAt")
      // When preselecting plan package options, we might get some global options duplicated.
      // Grouping by global option id will ensure we only display one of each pairing.
      .groupBy((rpo) => rpo.globalOption.id)
      .toValues()
      .map((rpos) => {
        // rpos is a mix of PP RPOs and DP RPOs, get just our rpos
        const dpRpo = rpos.find((rpo) => !ppRpos[rpo.id]);
        const firstRpo = rpos[0];
        const rpo = dpRpo ?? firstRpo;
        return {
          // If we have a DP RPO, use its ID. Otherwise, it's a preselected PP option (i.e., elevations for exterior DPs),
          // so use the global option ID. An ID is needed to properly display the option in the UI, and these global option IDs
          // will be dropped when saving. The BE will handle find-or-creating the option based on group, type, and name.
          id: dpRpo?.id ?? firstRpo.globalOption.id,
          globalOptionGroupId: rpo.globalOption.group.id,
          globalOptionId: rpo.globalOption.id,
          name: rpo.name,
          optionPrereqChildren: (dpRpo?.optionPrereqChildren ?? []).map((child) => ({
            id: child.id,
            globalOptionGroupId: child.globalOption.group.id,
            globalOptionId: child.globalOption.id,
            name: child.name,
          })),
        };
      })
  );
}
